/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { Button, Checkbox, Heading, Text } from "@breakingwave/react-ui-components";
import Column from "../../../components/grid/Column";
import Container from "../../../components/grid/Container";
import Row from "../../../components/grid/Row";
import Layout from "../../../components/layout";
import Spinner from "../../../components/spinner";
import { IManagedContext, ManagedProcessResourceTypeEnum, TaskGroupStatusesEnum } from "../../../types/shared";
import useNavigateError from "../../../hooks/useNavigateError";
import useNotifyAutomationService from "../../../api/notifyAutomationService";
import { ApproverTypeEnum, IJoinProjectApprovalPayload, IJoinProjectResponse, JoinProjectTaskAutomationKeyEnum } from "../../../types/joinProject";
import useGetManagedProcessByAccessToken from "../../../api/getManagedProcessByAccessToken";
import { footerProps, headerProps, htmlHeadProps } from "./JoinApproval.config";
import * as S from "./JoinApproval.styles";

const JoinApproval: React.FC = () => {
    const navigateError = useNavigateError();
    const [searchParams] = useSearchParams();

    const [managedContext, setManagedContext] = useState<IManagedContext>();
    const [accessToken, setAccessToken] = useState<string>();
    const [userId, setUserId] = useState<string>();
    const [termsCheckbox, setTermsCheckbox] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>();

    const {
        data: joinProject,
        isLoading: isGetProjectTeamAssignmentApprovalLoading
    } = useGetManagedProcessByAccessToken<IJoinProjectResponse>(managedContext, JoinProjectTaskAutomationKeyEnum.JOIN_REQUEST_APPROVED, accessToken, {
        enabled: !!(managedContext && accessToken) && !errorMessage
    });

    const approvalDataResponseRef = React.useRef(joinProject);
    approvalDataResponseRef.current = joinProject;

    const projectSlug = useMemo(() => joinProject?.ProjectSlug ?? "",[joinProject]);
    const joinApprovalConfirmationTask = useMemo(() => joinProject?.TaskItems.find((task) => task.TaskId === JoinProjectTaskAutomationKeyEnum.JOIN_REQUEST_APPROVED), [joinProject]);

    const {
        isLoading: isNotifyAutomationServiceLoading,
        mutateAsync: notifyAutomationService,
        isSuccess: isNotifyAutomationServiceSuccess
    } = useNotifyAutomationService(managedContext!, projectSlug, accessToken, JoinProjectTaskAutomationKeyEnum.JOIN_REQUEST_APPROVED);

    useEffect(() => {
        const { id, p, t, u } = Object.fromEntries([...searchParams]); 

        if (id && p && t && u) {
            setManagedContext({
                Id: id,
                ProjectSlugOrProjectId: p,
                ResourceType: ManagedProcessResourceTypeEnum.JoinProject
            });

            setAccessToken(t);
            setUserId(u);
        } else {
            navigateError(130);
        }
    }, []);

    useEffect(() => {
        const timeOut = setTimeout(() => {
            if (!approvalDataResponseRef.current) {
                setErrorMessage("This link may have expired.");
            }
        }, 60000);

        return (() => {
            clearTimeout(timeOut);
        });
    }, []);    

    const confirmApproval = (): void => {
        if (managedContext && accessToken && userId) {
            const joinProjectApprovalPayload: IJoinProjectApprovalPayload = {
                UserId: userId,
                ApproverType: ApproverTypeEnum.ProjectAdmin
            };

            notifyAutomationService(joinProjectApprovalPayload);
        }
    };

    return (
        <>
            {errorMessage &&
                <S.ErrorWrapper>
                    <h1>
                       An error has occurred whilst fetching the approval data
                    </h1>
                    <p>
                        {errorMessage}
                    </p>
                </S.ErrorWrapper>
            }

            {(!joinProject && !errorMessage) && (
                <S.LoadingSection>
                    <Spinner size={3} padding={"0px"} />
                    <h1>Please wait...</h1>
                </S.LoadingSection>
            )}
            {!isGetProjectTeamAssignmentApprovalLoading && joinProject && (
                    
                <Layout htmlHeadProps={htmlHeadProps} headerProps={headerProps} footerProps={footerProps}>
                    <S.PageTitleSection>
                        <Container isResponsive={true}>
                            <Row>
                                <Column>
                                    <Heading variant="h5" weight={300}>
                                        Request for additional team member
                                    </Heading>
                                    <S.Hr color="#595959" style={{ margin: "16px 0 24px" }} />
                                </Column>
                            </Row>
                            <Row>
                                <Column>
                                    <S.ProjectInfoGroup>
                                        <div>
                                            <Text size={14} weight={300} color="white.50">
                                                Project:
                                            </Text>
                                            <Text color="white.100">
                                                {joinProject.ProjectName}
                                            </Text>
                                        </div>
                                        <div>
                                            <Text size={14} weight={300} color="white.50">
                                                Requested by:
                                            </Text>
                                            <Text color="white.100">{joinProject.CreatedByName}</Text>
                                        </div>
                                        <div>
                                            <Text size={14} weight={300} color="white.50">
                                                Date of request:
                                            </Text>
                                            <Text color="white.100">
                                                {dayjs(joinProject.DateCreatedUtc).format(
                                                    "DD MMM YYYY"
                                                )}
                                            </Text>
                                        </div>
                                    </S.ProjectInfoGroup>
                                </Column>
                            </Row>
                        </Container>
                    </S.PageTitleSection>
                    <S.BodySection>
                        <Container isResponsive={true}>
                            <Row>
                                <Column md={8}>
                                    <Heading variant="h6" weight={300} style={{ paddingLeft: 12, marginBottom: 16 }}>
                                        Approval Summary
                                    </Heading>
                                    <S.Hr color="#595959" />
                                    <S.RequestDetailsList>
                                        <S.RequestDetailsListItem>
                                            <Text color="white.75">Team Member</Text>
                                            <Text>
                                                {joinProject.FirstName}{" "}
                                                {joinProject.LastName}
                                            </Text>
                                        </S.RequestDetailsListItem>
                                    </S.RequestDetailsList>
                                </Column>
                            </Row>
                            <Row>
                                <Column md={8}>
                                    <Heading variant="h6" weight={300} style={{ marginBottom: 4 }}>
                                        Terms
                                    </Heading>
                                    <Text color="white.50" style={{ marginBottom: 56 }}>
                                        Approval for this team member signals commitment to proceed in initiating the
                                        Breaking Wave process and may incur fees if cancelled.
                                        <br />
                                        <br />
                                        All subscriptions are rolling and charged monthly in arrears. They can be
                                        cancelled at any time with 48hrs notice to allow you flexibility.
                                    </Text>
                                </Column>
                                <Column md={8}>
                                    {isNotifyAutomationServiceSuccess ||
                                    joinApprovalConfirmationTask?.Status === TaskGroupStatusesEnum.Complete ? (
                                            <>
                                                <Button disabled style={{ marginBottom: 24 }}>
                                                Approved
                                                </Button>
                                                <Text>This has been approved</Text>
                                            </>
                                        ) : (
                                            <>
                                                <Checkbox
                                                    name="onboardingApprovalCheck"
                                                    onClick={() => setTermsCheckbox(!termsCheckbox)}
                                                >
                                                Please check this box if you are happy to proceed
                                                </Checkbox>
                                                <Button
                                                    isLoading={isNotifyAutomationServiceLoading}
                                                    onClick={confirmApproval}
                                                    disabled={!termsCheckbox || isNotifyAutomationServiceLoading}
                                                    style={{ marginTop: 40 }}
                                                >
                                                Approve
                                                </Button>
                                            </>
                                        )}
                                </Column>
                            </Row>
                        </Container>
                    </S.BodySection>
                </Layout>
            )}
        </>
    );
};

export default JoinApproval;
